import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { Typography } from '@mui/material';
import { AxiosResponse } from 'axios';
import classNames from 'classnames';
import { getServicesByCategory } from 'store/bill/actions';
import { resetOtherServicesList } from 'store/bill/reducer';
import { getServicesSelector } from 'store/bill/selectors';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { showToast } from 'store/toastify/reducer';
import { updateUserProfile } from 'store/user-service/actions';
import { getUserProfileSelector } from 'store/user-service/selectors';
import { Navbar } from 'components/Navbar';
import { ProviderServiceHeader, ServiceBillRequestFields } from 'components/OtherServices';
import { PaymentMethodModal } from 'components/PaymentMethodModal';
import { PaymentPayerForm } from 'components/PaymentPayerForm';
import { CustomButton, Loader, PayerDataDetailsModalContent } from 'components/shared';
import {
	FIREBASE_EVENT_ANALYTICS_BUTTON,
	FIREBASE_EVENT_ANALYTICS_PAGE,
	OTHER_PAYMENT_MAX_AMOUNT,
	OTHER_PAYMENT_MIN_AMOUNT,
	SERVICES_DEFAULT_REQUEST_BODY,
} from 'utils/constants';
import { LOCALE, PAY_TOKEN_TYPE, PAYMENT_PROVIDER, PROVIDER_TYPE, ROUTER_URL } from 'utils/enums';
import {
	convertCoinsToUAH,
	convertUAHToCoins,
	findPortmoneProviderAmountFieldValue,
	getAmountTitle,
	getChangedPayerProfileData,
	getData3DS,
	getErrorMessage,
	getMultiServicePaymentProviderData,
	getPayerFullName,
	getPaymentPayerInitialValues,
	getPaymentPayerServiceFieldsData,
	getPortmoneServiceReturnUrl,
	getServiceByIdRoute,
	getServicePaymentStatusRoute,
	getServicesByCategoryRoute,
	getUserFullName,
	isOtherPaymentAmountAllowed,
	normalizeServiceBillRequestData,
} from 'utils/helpers';
import { useOpen } from 'utils/hooks';
import { BillService, CardService, HostCardMessageService, RadabankService } from 'utils/services';
import {
	IBillCreateRequestArrayDataItem,
	ICardRequestResponse,
	ICreateBillRequest,
	IGetServiceDataResponse,
	INormalizedServiceBillRequestItem,
	IOtherServicePaymentRequest,
	IOtherServiceProviderServiceFieldsData,
	IPaymentPayerFormData,
	IPaymentPayerServiceFieldsData,
	IPaymentResponse,
	IServiceBillError,
	IServiceBillRequestData,
	IServicesItemResponse,
	ServiceFeeEntry,
	ServiceFeeRecord,
	StringOrNull,
	TypeOrNull,
} from 'utils/types';
import styles from './index.module.scss';

export const ServiceProviderPage: FC = () => {
	const history = useHistory();

	const { serviceId, categoryId } = useParams<{ serviceId: string; categoryId: string }>();

	const dispatch = useAppDispatch();

	const { isOpen, handleOpen, handleClose } = useOpen();

	const services = useAppSelector(getServicesSelector);
	const profile = useAppSelector(getUserProfileSelector);

	const [currentService, setCurrentService] = useState<IServicesItemResponse | null>(null);
	const [currentServiceData, setCurrentServiceData] = useState<IGetServiceDataResponse | null>(null);
	const [currentServiceFeeMap, setCurrentServiceFeeMap] = useState<{ fee: number; feeHostCard: number }>({
		fee: 0,
		feeHostCard: 0,
	});
	const [totalAmount, setTotalAmount] = useState(0);
	const [isLoading, setIsLoading] = useState(false);
	const [serviceRequestData, setServiceRequestData] = useState<IOtherServicePaymentRequest | null>(null);
	const [isShowDetails, setIsShowDetails] = useState(false);
	const [isShowPayerForm, setIsShowPayerForm] = useState(false);
	const [paymentPayerData, setPaymentPayerData] = useState<IPaymentPayerFormData | null>(null);
	const [createBillRequestData, setCreateBillRequestData] = useState<ICreateBillRequest | null>(null);
	const [billRequestData, setBillRequestData] = useState<IServiceBillRequestData | null>(null);

	// used for multibills to pay as separate bills like billtype THIRD
	const [billRequestServices, setBillRequestServices] = useState<INormalizedServiceBillRequestItem[]>([]);
	const [selectedServiceKeys, setSelectedServiceKeys] = useState<string[]>([]);
	// fee for services of multibills
	const [feeList, setFeeList] = useState<TypeOrNull<ServiceFeeRecord>>(null);

	const isMultipleBillsData = Array.isArray(billRequestData);
	const selectedServices = useMemo(
		() => billRequestServices.filter((service) => selectedServiceKeys.includes(service.selectionKey)),
		[billRequestServices, selectedServiceKeys]
	);
	const selectedPayableServices = useMemo(
		() => selectedServices.filter((service) => Number(service.incomeBalance) > 0),
		[selectedServices]
	);

	const getPaymentInstructionServiceFieldsData = (service: IBillCreateRequestArrayDataItem) => {
		const shortId = service.shortId ?? service.tariffItem.shortId;
		const reservedBillId = service.reservedBillId ?? service.tariffItem.reservedBillId;

		return {
			...(shortId !== undefined ? { shortId } : {}),
			...(reservedBillId ? { reservedBillId } : {}),
		};
	};

	const getPaymentRequestData = (reqData: IOtherServicePaymentRequest): IOtherServicePaymentRequest => {
		const payerServiceFieldsData = paymentPayerData ? getPaymentPayerServiceFieldsData(paymentPayerData) : {};

		return {
			...reqData,
			serviceProviderData: isMultipleBillsData
				? getMultiServicePaymentProviderData(selectedPayableServices, feeList, payerServiceFieldsData)
				: reqData.serviceProviderData.map((item) => {
						if (billRequestData && !('error' in billRequestData)) {
							return {
								...item,
								code: billRequestData.services.code,
								serviceId: billRequestData.services.id,
								providerService: billRequestData.services.name,
								billId: billRequestData.tariffItem?.id,
								serviceFieldsData: {
									...item.serviceFieldsData,
									...getPaymentInstructionServiceFieldsData(billRequestData),
									...payerServiceFieldsData,
								},
							};
						}

						return {
							...item,
							serviceFieldsData: {
								...item.serviceFieldsData,
								...payerServiceFieldsData,
							},
						};
				  }),
		};
	};

	useEffect(() => {
		return () => {
			dispatch(resetOtherServicesList());
		};
	}, []);

	useEffect(() => {
		if (!services.data.tableData.length) {
			getServices();
		} else if (!currentService) {
			const currentService = services.data.tableData.find((item: IServicesItemResponse) => item.id === serviceId);
			if (currentService) {
				setCurrentService(currentService);
			}
		}
	}, [services.data.tableData.length]);

	useEffect(() => {
		if (currentService && profile) {
			getCurrentServiceData(currentService.id, currentService.servicesContractsMap[0].contractId);

			const payload: IOtherServicePaymentRequest = {
				clientName: getUserFullName(profile),
				clientPhone: profile.phone,
				userId: profile.userId,
				returnUrl: getPortmoneServiceReturnUrl(categoryId, serviceId),
				serviceProviderData: [
					{
						code: currentService.code,
						serviceId: currentService.id,
						incomeBalance: convertUAHToCoins(0).toString(),
						fee: convertUAHToCoins(0).toString(),
						providerType: currentService.provider as PROVIDER_TYPE,
						providerService: currentService.name,
						serviceFieldsData: {},
					},
				],
			};

			setServiceRequestData(payload);
		}
	}, [profile?.userId, currentService]);

	useEffect(() => {
		if (totalAmount && serviceRequestData) {
			setServiceRequestData({
				...serviceRequestData,
				serviceProviderData: [
					{
						...serviceRequestData.serviceProviderData[0],
						incomeBalance: totalAmount.toString(),
						fee: currentServiceFeeMap.fee.toString(),
					},
				],
			});
		}
	}, [totalAmount, currentServiceFeeMap]);

	useEffect(() => {
		if (serviceRequestData) {
			setTotalAmount(+findPortmoneProviderAmountFieldValue(serviceRequestData));
		}
	}, [serviceRequestData?.serviceProviderData[0]?.serviceFieldsData]);

	const handleChangeServiceProviderData = (serviceFieldsData: IOtherServiceProviderServiceFieldsData) => {
		if (serviceRequestData) {
			const payload: IOtherServicePaymentRequest = {
				...serviceRequestData,
				serviceProviderData: [
					{
						...serviceRequestData.serviceProviderData[0],
						serviceFieldsData,
					},
				],
			};
			setServiceRequestData(payload);
		}
	};

	const getServices = async (): Promise<void> => {
		try {
			setIsLoading(true);
			await dispatch(
				getServicesByCategory({
					options: {
						...SERVICES_DEFAULT_REQUEST_BODY.options,
						serviceId: [serviceId],
						serviceCategoryId: [categoryId],
					},
					offset: 0,
					limit: 1,
					locale: LOCALE.UK,
				})
			).unwrap();
		} catch (error) {
			history.replace(ROUTER_URL.NOT_FOUND);
		} finally {
			setIsLoading(false);
		}
	};

	const getCurrentServiceData = async (serviceId: string, contractId: string): Promise<void> => {
		try {
			setIsLoading(true);

			const response: AxiosResponse<IGetServiceDataResponse> = await BillService.getServiceData({
				serviceId,
				contractId,
			});
			setCurrentServiceData(response.data);
		} catch (error) {
			history.replace(ROUTER_URL.ERROR);
		} finally {
			setIsLoading(false);
		}
	};

	const getServiceFee = async (amount: number): Promise<void> => {
		try {
			if (!currentService) return;

			if (amount <= 0) {
				setCurrentServiceFeeMap({ fee: 0, feeHostCard: 0 });
				setTotalAmount(amount);
				return;
			}

			const response = await BillService.getServiceFee(amount, currentService.code, currentService.provider);

			setCurrentServiceFeeMap({ ...response.data });
			setTotalAmount(amount);
		} catch (error) {
			dispatch(showToast({ message: getErrorMessage(error) }));
		}
	};

	const handlePayByCard = async (cardFromId: string, provider: StringOrNull): Promise<void> => {
		if (serviceRequestData) {
			let reqBody = { ...getPaymentRequestData(serviceRequestData), data3DS2: getData3DS(), cardFromId };

			if (provider === PAYMENT_PROVIDER.RADABANK) {
				const radabankData = await RadabankService.getInternalReqPayload({ userId: serviceRequestData.userId });
				const deviceData = await RadabankService.getReqPayload({});

				reqBody = {
					...reqBody,
					payToken: btoa(JSON.stringify({ ...radabankData, userId: undefined, ...deviceData })),
					payTokenType: PAY_TOKEN_TYPE.RADABANK,
					serviceProviderData: reqBody.serviceProviderData.map((item) => ({
						...item,
						fee: isMultipleBillsData
							? (feeList?.[item.serviceId]?.fee ?? 0).toString()
							: `${currentServiceFeeMap.feeHostCard}`,
					})),
				};
			}

			const response: AxiosResponse<IPaymentResponse[]> = await BillService.otherServicePay(reqBody);
			history.push(getServicePaymentStatusRoute(categoryId, serviceId, response.data[0].billId));
		}
	};

	const handlePayByWallet = async (payToken: string, payTokenType: PAY_TOKEN_TYPE): Promise<void> => {
		try {
			if (serviceRequestData) {
				const response: AxiosResponse<IPaymentResponse[]> = await BillService.otherServicePay({
					...getPaymentRequestData(serviceRequestData),
					payToken,
					payTokenType,
					data3DS2: getData3DS(),
				});
				history.push(getServicePaymentStatusRoute(categoryId, serviceId, response.data[0].billId));
			}
		} catch (err) {
			dispatch(showToast({ message: getErrorMessage(err) }));
		}
	};

	const handlePayByAnotherCard = async (): Promise<void> => {
		if (serviceRequestData) {
			if (isMultipleBillsData && selectedPayableServices.length > 1) {
				const createCardResponse: AxiosResponse<ICardRequestResponse> = await CardService.createRequest({
					userId: profile?.userId as string,
					returnUrl: `${process.env.REACT_APP_URL}${getServiceByIdRoute(categoryId, serviceId)}`,
				});

				// in an app-url-litener we close the browser for ios
				if (Capacitor.isNativePlatform()) {
					await Browser.open({ url: createCardResponse.data.link });
				} else {
					window.location.href = createCardResponse.data.link;
				}
				return;
			}
			const response: AxiosResponse<IPaymentResponse[]> = await BillService.otherServicePay(
				getPaymentRequestData(serviceRequestData)
			);
			const redirectUrl = response.data[0].link;

			if (Capacitor.isNativePlatform()) {
				await Browser.open({ url: redirectUrl });
			} else {
				window.location.href = redirectUrl;
			}
		}
	};

	const handleConfirmPayment = (): void => handleOpen();

	const handleCancelPaymentConfirmation = (): void => {
		setIsShowDetails(false);
		setIsShowPayerForm(true);
	};

	const getServiceDetailsData = async (): Promise<void> => {
		try {
			if (isMultipleBillsData) {
				if (!isOtherPaymentAmountAllowed(multiplePaymentTotalAmount)) {
					throw new Error(
						`Сума платежу повинна бути від ${getAmountTitle(OTHER_PAYMENT_MIN_AMOUNT)} до ${getAmountTitle(
							OTHER_PAYMENT_MAX_AMOUNT
						)}`
					);
				}
				setIsShowPayerForm(true);
				return;
			}
			setIsLoading(true);
			const totalAmount = +findPortmoneProviderAmountFieldValue(serviceRequestData as IOtherServicePaymentRequest);
			await getServiceFee(totalAmount);
			await HostCardMessageService.setNextActiveMessage();
			setIsShowPayerForm(true);
		} catch (error) {
			dispatch(showToast({ message: getErrorMessage(error) }));
		} finally {
			setIsLoading(false);
		}
	};

	const handleChangeFee = useCallback(({ fee, feeHostCard, serviceId }: ServiceFeeEntry) => {
		setFeeList((prev) => ({ ...prev, [serviceId]: { fee, feeHostCard } }));
	}, []);

	const handleGetBillRequestData = (value: TypeOrNull<IServiceBillRequestData>) => {
		setBillRequestData(value);

		if (!Array.isArray(value)) {
			setBillRequestServices([]);
			setSelectedServiceKeys([]);
			return;
		}

		const normalizedServices = normalizeServiceBillRequestData(value).map((service) => ({
			...service,
			tariffItem: {
				...service.tariffItem,
				amount: service.incomeBalance === null ? '' : convertCoinsToUAH(service.incomeBalance).toString(),
			},
		}));

		setBillRequestServices((currentServices) =>
			normalizedServices.map((service) => {
				const currentService = currentServices.find((item) => item.selectionKey === service.selectionKey);

				return currentService
					? {
							...service,
							incomeBalance: currentService.incomeBalance,
							amountToPay: currentService.amountToPay,
							tariffItem: { ...service.tariffItem, amount: currentService.tariffItem.amount },
					  }
					: service;
			})
		);
		setSelectedServiceKeys((currentKeys) => {
			const availableKeys = new Set(normalizedServices.map((service) => service.selectionKey));
			const preservedKeys = currentKeys.filter((key) => availableKeys.has(key));

			return preservedKeys.length
				? preservedKeys
				: normalizedServices
						.filter((service) => Number(service.incomeBalance) > 0)
						.map((service) => service.selectionKey);
		});
	};

	const handleChangeSelectedServices = (service: INormalizedServiceBillRequestItem) => {
		if (Number(service.incomeBalance) <= 0) return;

		setSelectedServiceKeys((prev) => {
			const isAlreadySelected = prev.includes(service.selectionKey);

			if (isAlreadySelected) {
				return prev.filter((key) => key !== service.selectionKey);
			}

			return [...prev, service.selectionKey];
		});
	};

	const handleChangeServiceAmount = (service: INormalizedServiceBillRequestItem, amount: string) => {
		const incomeBalance = amount === '' ? null : convertUAHToCoins(amount).toString();

		setBillRequestServices((services) =>
			services.map((item) =>
				item.selectionKey === service.selectionKey
					? {
							...item,
							incomeBalance,
							amountToPay: incomeBalance === null ? null : Number(incomeBalance),
							tariffItem: { ...item.tariffItem, amount },
					  }
					: item
			)
		);
		setSelectedServiceKeys((keys) => {
			if (Number(incomeBalance) > 0 && !keys.includes(service.selectionKey)) {
				return [...keys, service.selectionKey];
			}
			if (Number(incomeBalance) <= 0) {
				return keys.filter((key) => key !== service.selectionKey);
			}
			return keys;
		});
	};

	const multiplePaymentTotalAmount = useMemo(
		() => selectedPayableServices.reduce((acc, curr) => acc + convertCoinsToUAH(curr.incomeBalance as string), 0),
		[selectedPayableServices]
	);

	const multiplePaymentFee = useMemo(
		() => selectedPayableServices.reduce((acc, curr) => acc + Number(feeList?.[curr.selectionKey]?.fee), 0),
		[selectedPayableServices, feeList]
	);

	const multiplePaymentHostCardFee = useMemo(
		() => selectedPayableServices.reduce((acc, curr) => acc + Number(feeList?.[curr.selectionKey]?.feeHostCard), 0),
		[selectedPayableServices, feeList]
	);

	const paymentInstructionData = useMemo(() => {
		let billItems: IBillCreateRequestArrayDataItem[] = [];

		if (Array.isArray(billRequestData)) {
			billItems = selectedPayableServices;
		} else if (billRequestData && !('error' in billRequestData)) {
			billItems = [billRequestData];
		}
		return {
			number:
				billItems
					.map((item) => item.reservedBillId ?? item.tariffItem.reservedBillId)
					.filter(Boolean)
					.join(', ') || '—',
		};
	}, [billRequestData, selectedPayableServices]);

	const handleSubmitPayerForm = async (data: IPaymentPayerFormData): Promise<void> => {
		try {
			if (!profile || !createBillRequestData) {
				throw new Error('Не вдалося підготувати дані для створення платежу');
			}

			const changedProfileData = getChangedPayerProfileData(profile, data);
			if (changedProfileData) {
				await dispatch(updateUserProfile(changedProfileData)).unwrap();
			}

			const payerServiceFieldsData: IPaymentPayerServiceFieldsData = getPaymentPayerServiceFieldsData(data);
			const response = await BillService.createBillRequest({
				...createBillRequestData,
				serviceFieldsData: {
					...createBillRequestData.serviceFieldsData,
					...payerServiceFieldsData,
				},
			});
			let responseErrors: IServiceBillError[] = [];
			if (Array.isArray(response.data)) {
				responseErrors = response.data.filter((item) => 'error' in item) as IServiceBillError[];
			} else if ('error' in response.data) {
				responseErrors = [response.data];
			}

			if (responseErrors.length && (!Array.isArray(response.data) || responseErrors.length === response.data.length)) {
				throw new Error(responseErrors[0].error);
			}

			handleGetBillRequestData(response.data);
			setServiceRequestData((currentData) =>
				currentData ? { ...currentData, clientName: getPayerFullName(data) } : currentData
			);
			setPaymentPayerData(data);
			setIsShowPayerForm(false);
			setIsShowDetails(true);
		} catch (error) {
			dispatch(showToast({ message: getErrorMessage(error) }));
		}
	};

	return (
		<>
			<section className={classNames('page', styles.wrapper)}>
				{!!(currentService && currentServiceData) && (
					<div className="services-tab-content">
						<div className={classNames(styles.serviceData, { [styles.hidden]: isShowPayerForm && !!profile })}>
							<ServiceBillRequestFields
								onChangeFee={handleChangeFee}
								billRequestServices={billRequestServices}
								selectedServicesToPay={selectedServices}
								serviceData={currentServiceData}
								isShowDetails={isShowDetails}
								paymentPayerData={paymentPayerData}
								instructionNumber={paymentInstructionData.number}
								onChange={handleChangeServiceProviderData}
								onSubmit={getServiceDetailsData}
								serviceItemData={currentService}
								onClickBack={() => {
									if (isShowDetails) {
										handleCancelPaymentConfirmation();
										return;
									}
									history.push(getServicesByCategoryRoute(categoryId));
								}}
								onChangeSelectedServices={handleChangeSelectedServices}
								onChangeServiceAmount={handleChangeServiceAmount}
								onGetBillRequestData={handleGetBillRequestData}
								onGetCreateBillRequestData={setCreateBillRequestData}
							/>
							{!!isShowDetails && (
								<>
									<div className={styles.details}>
										<div className={styles.details__item}>
											<Typography variant="subtitle1">Сума:</Typography>
											<Typography variant="subtitle1">
												{getAmountTitle(
													isMultipleBillsData ? multiplePaymentTotalAmount : convertCoinsToUAH(totalAmount)
												)}
											</Typography>
										</div>
										<div className={styles.details__item}>
											<Typography variant="subtitle1">Комісія:</Typography>
											<Typography variant="subtitle1">
												{getAmountTitle(
													convertCoinsToUAH(isMultipleBillsData ? multiplePaymentFee : currentServiceFeeMap.fee)
												)}
											</Typography>
										</div>
										<div className={styles.details__item}>
											<Typography variant="subtitle1">Комісія при сплаті HostCard:</Typography>
											<Typography variant="subtitle1">
												{getAmountTitle(
													convertCoinsToUAH(
														isMultipleBillsData ? multiplePaymentHostCardFee : currentServiceFeeMap.feeHostCard
													)
												)}
											</Typography>
										</div>
									</div>
									<Typography className={styles.confirmationQuestion} variant="subtitle1">
										Чи підтверджуєте ви оплату?
									</Typography>
									<div className={styles.actions}>
										<CustomButton
											label="Підтвердити"
											onClick={handleConfirmPayment}
											disabled={
												isMultipleBillsData ? multiplePaymentTotalAmount <= 0 : !totalAmount || totalAmount <= 0
											}
											size="large"
										/>
										<CustomButton
											label="Скасувати"
											onClick={handleCancelPaymentConfirmation}
											fill="outline"
											color="primary"
											size="large"
										/>
									</div>
									<div className={styles.legal}>
										<div className={styles.note}>
											<a href="https://hostpay.com.ua/" target="_blank" rel="noreferrer">
												ТОВ «ХОСТПЕЙ»
											</a>
											<Typography variant="caption" color="inherit">
												, ЄДРПОУ: 45341126, Ліцензія НБУ №21/155-PK від 26.02.2024
											</Typography>
										</div>
										<div className={styles.note}>
											<Typography variant="caption" color="inherit">
												{` Операція за участю`}
											</Typography>
											<a href={process.env.REACT_APP_FINANSO_UA_URL} target="_blank" rel="noreferrer">
												{` ТОВ "ФІНАНСО УА"`}
											</a>
										</div>
									</div>
								</>
							)}
						</div>
						{isShowPayerForm && profile && (
							<>
								<ProviderServiceHeader
									onClickBack={() => setIsShowPayerForm(false)}
									title={currentService.title}
									icon={currentService.imageUrl ?? ''}
								/>
								<PaymentPayerForm
									initialValues={paymentPayerData ?? getPaymentPayerInitialValues(profile)}
									onSubmit={handleSubmitPayerForm}
									isLastNameFirst
									titleHint={<PayerDataDetailsModalContent />}
									titleHintAnalyticsPage={FIREBASE_EVENT_ANALYTICS_PAGE.PROFILE.SERVICES}
									titleHintAnalyticsButton={FIREBASE_EVENT_ANALYTICS_BUTTON.QUESTION_PAYER_DATA}
								/>
							</>
						)}
					</div>
				)}
			</section>
			{(isShowDetails || isMultipleBillsData || isShowPayerForm) && (
				<PaymentMethodModal
					isShowPaymentData
					pageName={FIREBASE_EVENT_ANALYTICS_PAGE.PROFILE.SERVICES}
					isOpen={isOpen}
					onClose={handleClose}
					totalHostCardFeeAmount={convertCoinsToUAH(
						isMultipleBillsData ? multiplePaymentHostCardFee : currentServiceFeeMap.feeHostCard
					)}
					totalFeeAmount={convertCoinsToUAH(isMultipleBillsData ? multiplePaymentFee : currentServiceFeeMap.fee)}
					totalAmount={isMultipleBillsData ? multiplePaymentTotalAmount : convertCoinsToUAH(totalAmount)}
					onPayByCard={handlePayByCard}
					onPayByAnotherCard={handlePayByAnotherCard}
					onPayByWallet={handlePayByWallet}
					allowedMethods={currentService?.paymentMethods ?? {}}
				/>
			)}
			<Navbar />
			{isLoading && <Loader />}
		</>
	);
};
